import React, { useState } from "react";
import { Stethoscope, CheckCircle, Eye, EyeOff } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import Loader from "../common_components/Loader";
import { useToast } from "../common_components/Toast";

const Register = () => {
  const navigate = useNavigate();
  const { register, isLoading: authLoading, apiRequest } = useAuth();
  const toast = useToast();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [otp, setOtp] = useState("");
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [isOtpVerified, setIsOtpVerified] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isOtpLoading, setIsOtpLoading] = useState(false);
  const [isOtpVerifying, setIsOtpVerifying] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.email) newErrors.email = "Email is required";
    if (!formData.password) newErrors.password = "Password is required";
    if (formData.password !== formData.confirmPassword)
      newErrors.confirmPassword = "Passwords do not match";
    if (!otp) newErrors.otp = "OTP is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSendOtp = async () => {
    if (!formData.email) {
      setErrors({ email: "Please enter your email first" });
      return;
    }
    setIsOtpLoading(true);
    try {
      const res = await apiRequest("/auth/send-otp", {
        method: "POST",
        body: JSON.stringify({ email: formData.email }),
      });
      if (res.message) {
        setIsOtpSent(true);
        setErrors({});
      } else {
        setErrors({ submit: res.error || res.message || "Failed to send OTP" });
      }
    } catch (e: any) {
      setErrors({
        submit:
          e.error || e.message || "Something went wrong while sending OTP",
      });
    } finally {
      setIsOtpLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    setIsOtpVerifying(true);
    try {
      const res = await apiRequest("/auth/verify-otp", {
        method: "POST",
        body: JSON.stringify({ email: formData.email, otp }),
      });
      if (res.message) {
        setIsOtpVerified(true);
        setErrors({});
      } else {
        toast.error(res.message || res.error || "Invalid OTP");
        setErrors({ submit: res.message || res.error || "Invalid OTP" });
      }
    } catch (e: any) {
      toast.error(
        e.error || e.message || "Something went wrong while verifying OTP"
      );
      setErrors({
        submit:
          e.error || e.message || "Something went wrong while verifying OTP",
      });
    } finally {
      setIsOtpVerifying(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm() || !isOtpVerified) {
      setErrors({ submit: "Please verify your email with OTP first" });
      return;
    }
    setIsLoading(true);
    try {
      const { confirmPassword, ...registrationData } = formData;
      const result = await register({
        ...registrationData,
        otp,
      });
      if (result.success) {
        setFormData({
          name: "",
          email: "",
          password: "",
          confirmPassword: "",
        });
        setOtp("");
        navigate("/allied/dashboard");
      } else {
        toast.error(result.error || "Registration failed");
        setErrors({ submit: result.error || "Registration failed" });
      }
    } catch (error: any) {
      toast.error(error?.message || "Registration failed");
      setErrors({ submit: "Unexpected error occurred" });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-slate-50 to-indigo-50 flex items-center justify-center p-4">
      {(authLoading || isLoading || isOtpLoading || isOtpVerifying) && (
        <Loader text="Processing..." />
      )}

      {/* Background Pattern */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-32 w-80 h-80 bg-blue-300 rounded-full opacity-30 blur-3xl"></div>
        <div className="absolute -bottom-40 -left-32 w-80 h-80 bg-indigo-300 rounded-full opacity-30 blur-3xl"></div>
      </div>

      <div className="relative w-full max-w-lg">
        <div className="bg-white/80 backdrop-blur-sm border border-slate-200 rounded-2xl shadow-2xl p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-2xl shadow-lg mb-4">
              <Stethoscope className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">
              Create Account
            </h1>
            <p className="text-slate-600 text-sm">
              Registration form - AIIMS Mangalagiri
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email + OTP */}
            <div className="w-full">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Email Address
              </label>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  name="email"
                  type="email"
                  className="flex-1 px-4 py-3 border border-slate-200 rounded-xl bg-slate-50/50 text-sm sm:text-base focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all hover:border-slate-300"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={isOtpSent}
                />
                <button
                  type="button"
                  onClick={handleSendOtp}
                  disabled={isOtpLoading || isOtpSent}
                  className={`px-6 py-3 rounded-xl text-white text-sm sm:text-base font-bold flex items-center justify-center transition-all shadow-lg ${
                    isOtpSent
                      ? "bg-slate-400 cursor-not-allowed"
                      : "bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 hover:shadow-xl"
                  }`}
                >
                  {isOtpLoading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                  ) : isOtpSent ? (
                    "OTP Sent"
                  ) : (
                    "Send OTP"
                  )}
                </button>
              </div>
              {errors.email && (
                <p className="text-sm text-red-600 mt-1 flex items-center gap-1">
                  <span className="w-1 h-1 bg-red-600 rounded-full"></span>
                  {errors.email}
                </p>
              )}
            </div>
            {/* OTP Box */}
            {isOtpSent && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Enter OTP
                </label>

                <div className="flex gap-2">
                  <input
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    className="flex-1 px-4 py-3 border border-slate-200 rounded-xl bg-slate-50/50 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all hover:border-slate-300"
                    placeholder="Enter 6-digit OTP"
                    disabled={isOtpVerified}
                  />
                  {!isOtpVerified && (
                    <button
                      type="button"
                      onClick={handleVerifyOtp}
                      disabled={isOtpVerifying}
                      className="px-6 py-3 rounded-xl bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-bold flex items-center justify-center transition-all shadow-lg hover:shadow-xl"
                    >
                      {isOtpVerifying ? (
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                      ) : (
                        "Verify"
                      )}
                    </button>
                  )}
                </div>

                {/* Subtle 'Change Email' Button Below */}
                {!isOtpVerified && (
                  <div className="flex justify-end mt-2">
                    <button
                      type="button"
                      onClick={() => {
                        setIsOtpSent(false);
                        setIsOtpVerified(false);
                        setOtp("");
                        setErrors({});
                      }}
                      className="text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors"
                    >
                      Change Email
                    </button>
                  </div>
                )}

                {errors.otp && (
                  <p className="text-sm text-red-600 mt-1">{errors.otp}</p>
                )}

                {isOtpVerified && (
                  <p className="text-sm text-green-600 flex items-center gap-1 mt-2">
                    <CheckCircle className="w-4 h-4" /> Email verified
                    successfully
                  </p>
                )}
              </div>
            )}

            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Full Name
              </label>
              <input
                name="name"
                type="text"
                className="w-full px-4 py-3 border border-slate-200 rounded-xl bg-slate-50/50 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all hover:border-slate-300"
                placeholder="Enter your full name"
                value={formData.name}
                onChange={handleChange}
              />
              {errors.name && (
                <p className="text-sm text-red-600 mt-1">{errors.name}</p>
              )}
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  name="password"
                  type={showPassword ? "text" : "password"}
                  className="w-full px-4 py-3 pr-12 border border-slate-200 rounded-xl bg-slate-50/50 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all hover:border-slate-300"
                  placeholder="Create a password"
                  value={formData.password}
                  onChange={handleChange}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600 transition-colors"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="text-sm text-red-600 mt-1">{errors.password}</p>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Confirm Password
              </label>
              <div className="relative">
                <input
                  name="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  className="w-full px-4 py-3 pr-12 border border-slate-200 rounded-xl bg-slate-50/50 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all hover:border-slate-300"
                  placeholder="Confirm your password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600 transition-colors"
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="text-sm text-red-600 mt-1">
                  {errors.confirmPassword}
                </p>
              )}
            </div>

            {/* Submit error */}
            {errors.submit && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">
                {errors.submit}
              </div>
            )}

            {/* Register button */}
            <button
              type="submit"
              disabled={isLoading || !isOtpVerified}
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 disabled:from-slate-400 disabled:to-slate-400 text-white font-bold py-3 rounded-xl flex items-center justify-center transition-all shadow-lg hover:shadow-xl disabled:shadow-none"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2"></div>
                  Creating Account...
                </>
              ) : (
                "Create Account"
              )}
            </button>

            {/* Navigate to Login */}
            <div className="mt-4 text-center">
              <p className="text-sm text-slate-600">
                Already have an account?{" "}
                <button
                  type="button"
                  onClick={() => navigate("/login")}
                  className="text-blue-600 font-semibold hover:text-blue-800 transition-colors"
                >
                  Login
                </button>
              </p>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="mt-6 text-center">
          <p className="text-xs text-slate-500">
            © 2025 AIIMS Mangalagiri. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
